import { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap, FeatureGroup } from "react-leaflet";
import L from "leaflet";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import "leaflet/dist/leaflet.css";
import {
  Map,
  Pencil,
  Square,
  Minus,
  Trash2,
  Eye,
  EyeOff,
  Save,
  Layers,
  ZoomIn,
  ZoomOut,
  Home,
  MapPin,
  Hexagon,
  Route,
} from "lucide-react";
import { Button } from "./button";
import { Label } from "./label";
import { Switch } from "./switch";
import { Badge } from "./badge";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./card";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Layer styling constants
const LAYER_STYLES = {
  LOT_BOUNDARY: {
    color: "#2F4F4F", // Evergreen
    weight: 3,
    fillOpacity: 0.1,
    fillColor: "#2F4F4F",
    dashArray: null,
  },
  BUILDING_ENVELOPE: {
    color: "#C49A6C", // Copper
    weight: 2,
    fillOpacity: 0.15,
    fillColor: "#C49A6C",
    dashArray: null,
  },
  EASEMENT: {
    color: "#6B7280", // Muted gray
    weight: 2,
    fillOpacity: 0.1,
    fillColor: "#6B7280",
    dashArray: "5, 5", // Dashed
  },
  ACCESS: {
    color: "#4B5563", // Solid gray
    weight: 2,
    fillOpacity: 0,
    dashArray: null,
  },
};

// Horse Mountain Ranch approximate center (Colorado)
const DEFAULT_CENTER = [39.6, -106.1];
const DEFAULT_ZOOM = 14;

// Geoman Drawing Controls Component
const GeomanControls = ({ onGeometryCreated, onGeometryEdited, activeLayer, setActiveLayer }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Initialize Geoman
    map.pm.addControls({
      position: "topleft",
      drawMarker: false,
      drawCircleMarker: false,
      drawCircle: false,
      drawText: false,
      cutPolygon: false,
      rotateMode: false,
    });

    // Set global options
    map.pm.setGlobalOptions({
      snappable: true,
      snapDistance: 15,
      allowSelfIntersection: false,
    });

    // Handle geometry creation
    map.on("pm:create", (e) => {
      const layer = e.layer;
      const geoJSON = layer.toGeoJSON();
      
      // Apply style based on active layer
      const style = LAYER_STYLES[activeLayer] || LAYER_STYLES.LOT_BOUNDARY;
      layer.setStyle(style);
      
      // Add layer type to properties
      geoJSON.properties = {
        ...geoJSON.properties,
        layer_type: activeLayer,
      };
      
      if (onGeometryCreated) {
        onGeometryCreated(geoJSON, activeLayer);
      }
    });

    // Handle geometry edit
    map.on("pm:edit", (e) => {
      const layer = e.layer;
      const geoJSON = layer.toGeoJSON();
      
      if (onGeometryEdited) {
        onGeometryEdited(geoJSON);
      }
    });

    return () => {
      map.pm.removeControls();
      map.off("pm:create");
      map.off("pm:edit");
    };
  }, [map, activeLayer, onGeometryCreated, onGeometryEdited]);

  // Update draw style when active layer changes
  useEffect(() => {
    if (!map) return;
    
    const style = LAYER_STYLES[activeLayer] || LAYER_STYLES.LOT_BOUNDARY;
    map.pm.setGlobalOptions({
      pathOptions: style,
    });
  }, [map, activeLayer]);

  return null;
};

// Map zoom/center controls
const MapControls = ({ center, zoom }) => {
  const map = useMap();

  const handleZoomIn = () => map.zoomIn();
  const handleZoomOut = () => map.zoomOut();
  const handleReset = () => map.setView(center, zoom);

  return (
    <div className="absolute bottom-4 right-4 z-[1000] flex flex-col gap-1">
      <Button
        variant="secondary"
        size="icon"
        onClick={handleZoomIn}
        className="h-8 w-8 shadow-md"
      >
        <ZoomIn className="w-4 h-4" />
      </Button>
      <Button
        variant="secondary"
        size="icon"
        onClick={handleZoomOut}
        className="h-8 w-8 shadow-md"
      >
        <ZoomOut className="w-4 h-4" />
      </Button>
      <Button
        variant="secondary"
        size="icon"
        onClick={handleReset}
        className="h-8 w-8 shadow-md"
      >
        <Home className="w-4 h-4" />
      </Button>
    </div>
  );
};

/**
 * GISMapEditor - Interactive GIS map with drawing tools
 * 
 * Props:
 * - propertyId: ID of the property being edited
 * - initialGeometries: Array of existing geometries
 * - onSave: Callback when geometries are saved
 * - center: Map center coordinates
 * - zoom: Initial zoom level
 * - readOnly: Disable editing
 */
const GISMapEditor = ({
  propertyId,
  initialGeometries = [],
  initialLotBoundary = null,
  initialCentroid = null,
  onSave,
  onGeometryChange,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  readOnly = false,
  height = "500px",
}) => {
  const [geometries, setGeometries] = useState(initialGeometries);
  const [lotBoundary, setLotBoundary] = useState(initialLotBoundary);
  const [centroid, setCentroid] = useState(initialCentroid);
  const [activeLayer, setActiveLayer] = useState("LOT_BOUNDARY");
  const [visibleLayers, setVisibleLayers] = useState({
    LOT_BOUNDARY: true,
    BUILDING_ENVELOPE: true,
    EASEMENT: true,
    ACCESS: true,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const mapRef = useRef(null);

  // Calculate centroid from polygon
  const calculateCentroid = (geometry) => {
    if (!geometry || geometry.type !== "Polygon") return null;
    
    const coords = geometry.coordinates[0];
    if (!coords || coords.length === 0) return null;
    
    const lonSum = coords.reduce((sum, c) => sum + c[0], 0);
    const latSum = coords.reduce((sum, c) => sum + c[1], 0);
    
    return {
      type: "Point",
      coordinates: [lonSum / coords.length, latSum / coords.length],
    };
  };

  // Handle new geometry created
  const handleGeometryCreated = useCallback((geoJSON, layerType) => {
    if (layerType === "LOT_BOUNDARY") {
      setLotBoundary(geoJSON.geometry);
      setCentroid(calculateCentroid(geoJSON.geometry));
    } else {
      setGeometries((prev) => [
        ...prev,
        {
          geometry_id: `temp_${Date.now()}`,
          geometry_type: layerType,
          geometry: geoJSON.geometry,
          description: "",
        },
      ]);
    }
    setHasChanges(true);
    
    if (onGeometryChange) {
      onGeometryChange({
        lotBoundary: layerType === "LOT_BOUNDARY" ? geoJSON.geometry : lotBoundary,
        centroid: layerType === "LOT_BOUNDARY" ? calculateCentroid(geoJSON.geometry) : centroid,
        geometries: layerType === "LOT_BOUNDARY" ? geometries : [...geometries, { geometry_type: layerType, geometry: geoJSON.geometry }],
      });
    }
  }, [lotBoundary, centroid, geometries, onGeometryChange]);

  // Handle geometry edited
  const handleGeometryEdited = useCallback((geoJSON) => {
    setHasChanges(true);
  }, []);

  // Toggle layer visibility
  const toggleLayerVisibility = (layerType) => {
    setVisibleLayers((prev) => ({
      ...prev,
      [layerType]: !prev[layerType],
    }));
  };

  // Delete all geometries of a type
  const clearLayer = (layerType) => {
    if (layerType === "LOT_BOUNDARY") {
      setLotBoundary(null);
      setCentroid(null);
    } else {
      setGeometries((prev) => prev.filter((g) => g.geometry_type !== layerType));
    }
    setHasChanges(true);
  };

  // Save all geometries
  const handleSave = () => {
    if (onSave) {
      onSave({
        gis_centroid: centroid,
        gis_lot_boundary: lotBoundary,
        geometries: geometries,
      });
    }
    setHasChanges(false);
    toast.success("GIS data saved");
  };

  // Create GeoJSON for display
  const getGeoJSONData = () => {
    const features = [];

    // Add lot boundary
    if (lotBoundary && visibleLayers.LOT_BOUNDARY) {
      features.push({
        type: "Feature",
        properties: { layer_type: "LOT_BOUNDARY" },
        geometry: lotBoundary,
      });
    }

    // Add other geometries
    geometries.forEach((geom) => {
      if (visibleLayers[geom.geometry_type]) {
        features.push({
          type: "Feature",
          properties: {
            layer_type: geom.geometry_type,
            geometry_id: geom.geometry_id,
          },
          geometry: geom.geometry,
        });
      }
    });

    return {
      type: "FeatureCollection",
      features,
    };
  };

  // Style function for GeoJSON layers
  const styleFeature = (feature) => {
    const layerType = feature.properties?.layer_type || "LOT_BOUNDARY";
    return LAYER_STYLES[layerType] || LAYER_STYLES.LOT_BOUNDARY;
  };

  const layerButtons = [
    { type: "LOT_BOUNDARY", label: "Lot Boundary", icon: Hexagon, color: "#2F4F4F" },
    { type: "BUILDING_ENVELOPE", label: "Building Envelope", icon: Square, color: "#C49A6C" },
    { type: "EASEMENT", label: "Easement", icon: Minus, color: "#6B7280" },
    { type: "ACCESS", label: "Driveway / Access", icon: Route, color: "#4B5563" },
  ];

  return (
    <div className="space-y-4" data-testid="gis-map-editor">
      {/* Layer Controls */}
      {!readOnly && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Drawing Tools
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Active Layer Selection */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">
                Select layer to draw:
              </Label>
              <div className="flex flex-wrap gap-2">
                {layerButtons.map(({ type, label, icon: Icon, color }) => (
                  <Button
                    key={type}
                    variant={activeLayer === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveLayer(type)}
                    className="gap-2"
                    style={{
                      borderColor: activeLayer === type ? color : undefined,
                      backgroundColor: activeLayer === type ? color : undefined,
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Layer Visibility Toggles */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">
                Layer visibility:
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {layerButtons.map(({ type, label, color }) => (
                  <div
                    key={type}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm">{label}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => toggleLayerVisibility(type)}
                      >
                        {visibleLayers[type] ? (
                          <Eye className="w-3 h-3" />
                        ) : (
                          <EyeOff className="w-3 h-3" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-destructive"
                        onClick={() => clearLayer(type)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            {hasChanges && (
              <Button onClick={handleSave} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                Save GIS Data
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Map Container */}
      <div
        className="relative rounded-lg overflow-hidden border"
        style={{ height }}
      >
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: "100%", width: "100%" }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            opacity={0.3}
          />

          {/* Display existing geometries */}
          <GeoJSON
            key={JSON.stringify(getGeoJSONData())}
            data={getGeoJSONData()}
            style={styleFeature}
            onEachFeature={(feature, layer) => {
              const layerType = feature.properties?.layer_type;
              layer.bindPopup(`<strong>${layerType?.replace("_", " ") || "Geometry"}</strong>`);
            }}
          />

          {/* Display centroid marker */}
          {centroid && visibleLayers.LOT_BOUNDARY && (
            <GeoJSON
              data={{
                type: "Feature",
                geometry: centroid,
                properties: { type: "centroid" },
              }}
              pointToLayer={(feature, latlng) =>
                L.marker(latlng, {
                  icon: L.divIcon({
                    className: "centroid-marker",
                    html: `<div style="width: 12px; height: 12px; background: #2F4F4F; border: 2px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
                    iconSize: [12, 12],
                    iconAnchor: [6, 6],
                  }),
                })
              }
            />
          )}

          {/* Geoman drawing controls */}
          {!readOnly && (
            <GeomanControls
              onGeometryCreated={handleGeometryCreated}
              onGeometryEdited={handleGeometryEdited}
              activeLayer={activeLayer}
              setActiveLayer={setActiveLayer}
            />
          )}

          {/* Map controls */}
          <MapControls center={center} zoom={zoom} />
        </MapContainer>

        {/* Layer Legend */}
        <div className="absolute top-4 right-4 z-[1000] bg-white/90 backdrop-blur rounded-lg p-3 shadow-md">
          <h4 className="text-xs font-semibold mb-2 flex items-center gap-1">
            <Layers className="w-3 h-3" /> Legend
          </h4>
          <div className="space-y-1">
            {layerButtons.map(({ type, label, color }) => (
              <div key={type} className="flex items-center gap-2 text-xs">
                <div
                  className="w-4 h-1 rounded"
                  style={{
                    backgroundColor: color,
                    borderStyle: type === "EASEMENT" ? "dashed" : "solid",
                    borderWidth: type === "EASEMENT" ? "1px" : "0",
                    borderColor: color,
                  }}
                />
                <span className={!visibleLayers[type] ? "text-muted-foreground line-through" : ""}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* GIS Info Summary */}
      <div className="flex flex-wrap gap-2">
        {lotBoundary && (
          <Badge variant="outline" className="gap-1">
            <Hexagon className="w-3 h-3" style={{ color: "#2F4F4F" }} />
            Lot Boundary
          </Badge>
        )}
        {centroid && (
          <Badge variant="outline" className="gap-1">
            <MapPin className="w-3 h-3" style={{ color: "#2F4F4F" }} />
            Centroid Set
          </Badge>
        )}
        {geometries.filter((g) => g.geometry_type === "BUILDING_ENVELOPE").length > 0 && (
          <Badge variant="outline" className="gap-1">
            <Square className="w-3 h-3" style={{ color: "#C49A6C" }} />
            Building Envelope
          </Badge>
        )}
        {geometries.filter((g) => g.geometry_type === "EASEMENT").length > 0 && (
          <Badge variant="outline" className="gap-1">
            <Minus className="w-3 h-3" style={{ color: "#6B7280" }} />
            {geometries.filter((g) => g.geometry_type === "EASEMENT").length} Easement(s)
          </Badge>
        )}
        {geometries.filter((g) => g.geometry_type === "ACCESS").length > 0 && (
          <Badge variant="outline" className="gap-1">
            <Route className="w-3 h-3" style={{ color: "#4B5563" }} />
            Access/Driveway
          </Badge>
        )}
      </div>
    </div>
  );
};

export { GISMapEditor, LAYER_STYLES, DEFAULT_CENTER, DEFAULT_ZOOM };
